import type { DetailsSectionProps } from '@hotjar/feedback/components/common/Wizard/Sections/Details/DetailsSection';
import type { IntegrationsSectionProps } from '@hotjar/feedback/components/common/Wizard/Sections/Integrations/IntegrationsSection';
import type { BehaviorSectionProps } from '@hotjar/feedback/components/Surveys/Wizard/Behavior/BehaviorSection/BehaviorSection';
import type { SurveyInvitationProps } from '@hotjar/feedback/components/Surveys/Wizard/Invitation/Invitation';
import type { SurveyLocationProps } from '@hotjar/feedback/components/Surveys/Wizard/Location/SurveyLocation';
import type { QuestionsSectionProps } from '@hotjar/feedback/components/Surveys/Wizard/Questions/QuestionsSection/QuestionsSection';
import type { AppearanceSectionProps } from '@hotjar/feedback/components/Surveys/Wizard/Sections/Appearance/AppearanceSection';
import type { SummarySectionProps } from '@hotjar/feedback/components/Surveys/Wizard/Summary/SummarySection';
import type { TargetingSectionProps } from '@hotjar/feedback/components/Surveys/Wizard/Targeting/TargetingSection';
import type { WidgetPreviewProps } from '@hotjar/feedback/components/common/Wizard/WidgetPreview/WidgetPreview';

enum AccordionSectionStatus {
  ERROR = 0,
  SUCCESS = 1,
}

// TODO: change 'name' back to 'details'. Changing it now to make migration simpler
type CommonStepKey = 'name' | 'targeting' | 'forwarding' | 'review';

export type SurveySectionKey =
  | CommonStepKey
  | 'location'
  | 'questions'
  | 'appearance'
  | 'behavior'
  | 'invitation';

export type FeedbackSectionKey = CommonStepKey | 'steps';

export type WizardSectionKey = SurveySectionKey | FeedbackSectionKey;

export const SectionStatus = AccordionSectionStatus;

export interface WizardSection<T extends WizardSectionKey> {
  key: T;
  mainContent: JSX.Element | null;
  onToggle?: (isOpen: boolean) => void;
  sideContent?: JSX.Element | null;
  status: AccordionSectionStatus | undefined;
  title: string;
  summary: string | JSX.Element;
}

export interface WizardStep<T extends WizardSectionKey> extends WizardSection<T> {
  isDone: boolean;
  onDone: () => void;
  onNext: () => void;
}

export interface WizardSummary extends WizardSection<'review'> {
  key: 'review';
}

export interface LegacyWizardSection {
  name: SurveySectionKey;
  nextSection: SurveySectionKey;
  invalidMessage: string;
  open: boolean;
  openedOnce: boolean;
  success: boolean | null;
  stepNumber: number;
  validate: () => void;
}

export type LegacyWizardSections = Record<SurveySectionKey, LegacyWizardSection>;

export type SurveySectionProps = DetailsSectionProps &
  SurveyLocationProps &
  QuestionsSectionProps &
  AppearanceSectionProps &
  WidgetPreviewProps &
  SurveyInvitationProps &
  BehaviorSectionProps &
  IntegrationsSectionProps &
  TargetingSectionProps &
  SummarySectionProps & {
    activeForwardingNames: string;
  };
